/**
 * @CODE:SPEC-ACC-002-HOOK-COURSE-001
 * useCoursesForTrail Hook
 *
 * Fetches courses for a specific trail
 */

import { useQuery } from "@tanstack/react-query";
import { getCoursesForTrail } from "../api/accommodation-trail.api";
import type { Course } from "../types/accommodation.types";

interface UseCoursesForTrailOptions {
  /** Trail ID to fetch courses for */
  trailId?: string;
  /** Whether to fetch courses */
  enabled?: boolean;
}

interface UseCoursesForTrailResult {
  /** Courses for the trail */
  courses: Course[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Whether data is available */
  hasData: boolean;
}

/**
 * Hook to fetch courses for a specific trail
 *
 * @example
 * ```tsx
 * const { courses, isLoading } = useCoursesForTrail({
 *   trailId: 'T_THEME_MNG0000011235'
 * });
 * ```
 */
export function useCoursesForTrail({
  trailId,
  enabled = true,
}: UseCoursesForTrailOptions): UseCoursesForTrailResult {
  const {
    data: courses = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["courses", "trail", trailId],
    queryFn: () => {
      if (!trailId) {
        return Promise.resolve([]);
      }
      return getCoursesForTrail(trailId);
    },
    enabled: enabled && !!trailId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });

  return {
    courses,
    isLoading,
    error: error as Error | null,
    hasData: courses.length > 0,
  };
}
